import React, { useRef, useImperativeHandle, useEffect } from 'react';
import useInputController, { isEmpty, createInputValidator } from '../hooks/useInputController';

import type { InputHTMLAttributes } from 'react';

const NUMBER_REGEXP = /^\s*(-|\+)?(\d+|(\d*(\.\d*)))\s*$/;

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'onBlur'> & {
	decimals?: number;
	type: 'number';
	value: number | string;
	onChange: (value: string | number) => void;
	debounce?: number;
	/** Uses native addEventListener — needed because checkValidity() behaves differently in React's synthetic blur */
	onBlur?: (event: Event) => void;
};

const PurchaseCostInput = React.forwardRef<HTMLInputElement, Props>(
	(
		{ decimals = 2, value, onChange, debounce, onKeyPress: _onKeyPress, onBlur: externalOnBlur, ...inputProps },
		forwardRef
	) => {
		const ref = useRef<HTMLInputElement>(null);

		useImperativeHandle(forwardRef, () => ref.current!);

		// Native blur event is used because event.target.checkValidity() returns different
		// results in React's synthetic blur vs the native event
		useEffect(() => {
			if (externalOnBlur) {
				const element = ref.current;
				element?.addEventListener('blur', externalOnBlur);
				return () => element?.removeEventListener('blur', externalOnBlur);
			}
		}, [externalOnBlur]);

		const controlled = useInputController<number | string>(value, onChange, {
			name: inputProps.name,
			debounce,
			format: v => {
				if (isEmpty(v)) return '';
				return parseFloat(parseFloat(v as string).toFixed(decimals)).toString();
			},
			parse: v => {
				if (isEmpty(v)) return null as unknown as number;
				if (NUMBER_REGEXP.test(v)) return parseFloat(v);
				return undefined as unknown as number;
			},
			validate: createInputValidator(inputProps)
		});

		function onKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
			if (event.keyCode === 32) {
				event.preventDefault();
			}
			_onKeyPress?.(event);
		}

		function onBlur() {
			controlled.onBlur();
		}

		return (
			<input
				{...inputProps}
				type="number"
				value={controlled.value}
				onChange={controlled.onChange}
				onBlur={onBlur}
				onKeyPress={onKeyPress}
				ref={ref}
			/>
		);
	}
);

export default PurchaseCostInput;
